import { Component, ElementRef, Input, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { go } from 'fuzzysort';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MaterialType } from 'src/app/classes/material-type.class';
import { RawMaterial } from 'src/app/classes/raw-material.class';
import { Material } from '../../../../classes/material.class';
import { CodaltComponent } from '../../../../codalt.component';
import { PlanningFormData } from '../../../../services/entity-type.service';
import {
    flattenSelectedOptionals,
    GroupedOptional,
    groupOptionalsByRelatedType,
    isOptionalSelectionValid as checkOptionalSelectionValid,
    MaterialService,
} from '../../../../services/material/material.service';

@Component({
    selector: 'app-trackline-planning-materials',
    standalone: false,
    templateUrl: './trackline-planning-materials.component.html',
    styleUrl: './trackline-planning-materials.component.scss',
})
export class TracklinePlanningMaterialsComponent extends CodaltComponent implements OnInit {
    @Input() fieldFormData: PlanningFormData;

    @ViewChild('materialSelectionDialog') materialSelectionDialog: TemplateRef<any>;
    @ViewChild('optionalSelectionDialog') optionalSelectionDialog: TemplateRef<any>;
    @ViewChildren('quantityInput') quantityInputs: QueryList<ElementRef<HTMLInputElement>>;

    allMaterials: Material[] = [];
    materialTypes: MaterialType[] = [];
    rawMaterials: RawMaterial[] = [];
    optionalMaterials: RawMaterial[] = [];
    groupedOptionals: GroupedOptional[] = [];
    selectedOptionalsMap: Map<number, RawMaterial | RawMaterial[]> = new Map();
    searchControl = new FormControl('');
    filteredMaterials: Material[] = [];
    dialogRef: MatDialogRef<any>[] = [];
    planningMaterialsArray: FormArray;
    selectedMaterial: Material;

    constructor(
        private materialService: MaterialService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        this.initFormArray();
        this.loadMaterials();
        this.loadMaterialTypes();
        this.initSearchControl();
    }

    private initSearchControl() {
        this.subscriptions.add(
            this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(query => {
                this.filterMaterials(query);
            })
        );
    }

    private initFormArray() {
        if (!this.fieldFormData.form.contains('planningMaterials')) {
            (this.fieldFormData.form as any).addControl('planningMaterials', new FormArray([]));
        }

        this.planningMaterialsArray = (this.fieldFormData.form as any).get('planningMaterials') as FormArray;

        // Load existing planning materials if editing
        if (this.fieldFormData.orginal?.planning_materials?.length) {
            this.fieldFormData.orginal.planning_materials.forEach(pm => {
                this.addMaterialRow(pm.material, pm.quantity, pm.id);
            });
        }
    }

    private loadMaterials() {
        this.subscriptions.add(
            this.materialService.getList().subscribe(materials => {
                this.allMaterials = materials || [];
                this.filteredMaterials = materials || [];
            })
        );
    }

    private loadMaterialTypes() {
        this.subscriptions.add(
            this.materialService.getMaterialTypes().subscribe(materialTypes => {
                this.materialTypes = materialTypes || [];
            })
        );
        this.subscriptions.add(
            this.materialService.getAllRawMaterials().subscribe(rawMaterials => {
                this.rawMaterials = rawMaterials || [];
            })
        );
    }

    openAddModal() {
        this.searchControl.setValue('', { emitEvent: false });

        this.filteredMaterials = [...this.allMaterials];

        this.dialogRef.push(
            this.dialog.open(this.materialSelectionDialog, {
                width: '600px',
                height: '99vh',
                maxHeight: '750px',
            })
        );
    }

    filterMaterials(query: string) {
        if (!query?.length) {
            this.filteredMaterials = this.allMaterials;
            return;
        }

        const searchData = this.allMaterials.map(material => {
            let searchString = material.name ? material.name + ' ' : '';

            if (Array.isArray(material.raw_materials)) {
                for (const raw of material.raw_materials) {
                    if (raw?.name) searchString += raw.name + ' ';

                    if (raw?.color) {
                        if (raw.color.name) searchString += raw.color.name + ' ';
                        if (raw.color.ral) searchString += raw.color.ral + ' ';
                    }

                    if (raw?.supplier?.name) searchString += raw.supplier.name + ' ';
                    if (raw?.material_type?.name) searchString += raw.material_type.name + ' ';
                }
            }

            return { material, searchString };
        });

        this.filteredMaterials = go(query, searchData, {
            key: 'searchString',
            threshold: -10000,
        }).map(result => result.obj.material);
    }

    selectMaterial(material: Material) {
        material.plannings_count++;
        const optionalMaterials = material.raw_materials?.filter(
            (rm: RawMaterial & { pivot?: { optional?: boolean } }) =>
                rm.material_type?.main === false && rm.pivot?.optional
        );
        const mainRawMaterial = material.raw_materials?.find(rm => rm.material_type?.main === true);
        const type = this.materialTypes.find(mt => mt.id === mainRawMaterial?.material_type_id);

        if (optionalMaterials?.length > 0) {
            this.optionalMaterials = optionalMaterials;
            this.selectedMaterial = material;
            this.groupedOptionals = groupOptionalsByRelatedType(
                optionalMaterials,
                type,
                this.materialTypes,
                this.rawMaterials
            );
            this.selectedOptionalsMap = new Map();

            this.dialogRef.push(
                this.dialog.open(this.optionalSelectionDialog, {
                    width: '600px',
                    maxHeight: '99vh',
                    data: { material },
                })
            );

            return;
        }

        this.addMaterialRow(material);

        this.dialogRef.pop().close();
    }

    isSelectedCheckbox(materialTypeId: number, raw: RawMaterial): boolean {
        const sel = this.selectedOptionalsMap.get(materialTypeId);
        return Array.isArray(sel) && (sel as RawMaterial[]).some(r => r.id === raw.id);
    }
    toggleCheckbox(materialTypeId: number, raw: RawMaterial) {
        const current = (this.selectedOptionalsMap.get(materialTypeId) as RawMaterial[]) || [];
        const idx = current.findIndex(r => r.id === raw.id);
        if (idx >= 0) {
            this.selectedOptionalsMap.set(
                materialTypeId,
                current.filter((_, i) => i !== idx)
            );
        } else {
            this.selectedOptionalsMap.set(materialTypeId, [...current, raw]);
        }
    }
    selectOptionalMaterial(material: Material) {
        const mainRawMaterial = material.raw_materials?.find(
            (rm: RawMaterial & { pivot?: { optional?: number } }) => rm.material_type?.main === true
        );

        const nonOptionalRawMaterials =
            material.raw_materials?.filter(
                (rm: RawMaterial & { pivot?: { optional?: number } }) =>
                    rm.material_type?.main === false && !rm.pivot?.optional
            ) || [];

        const selectedFromMap = flattenSelectedOptionals(this.selectedOptionalsMap);

        this.addMaterialRow({
            ...material,
            raw_materials: [mainRawMaterial, ...nonOptionalRawMaterials, ...selectedFromMap],
        });

        this.dialogRef.forEach(dialog => dialog.close());
        this.dialogRef = [];

        this.selectedMaterial = null;
    }

    private addMaterialRow(material: Material, quantity?: number, id?: number) {
        const materialGroup = new FormGroup({
            id: new FormControl(id),
            material_id: new FormControl(material.id, Validators.required),
            raw_material_ids: new FormControl(material.raw_materials?.map(rm => rm.id) || []),
            material: new FormControl(material),
            quantity: new FormControl(quantity, [Validators.required, Validators.min(0)]),
        });

        // Prevent pushing enabled FormGroup cause that flips the whole (parent) form back to enabled
        if (this.fieldFormData.form.disabled) {
            materialGroup.disable({ emitEvent: false });
        }

        this.planningMaterialsArray.push(materialGroup);

        this.fieldFormData.form.markAsTouched();
        this.fieldFormData.form.markAsDirty();

        if (!quantity) {
            setTimeout(() => {
                this.quantityInputs.last?.nativeElement?.focus();
            });
        }
    }

    removeMaterial(index: number) {
        this.planningMaterialsArray.removeAt(index);

        this.fieldFormData.form.markAsDirty();
    }

    isFormDisabled(): boolean {
        return this.fieldFormData.form.disabled;
    }

    isOptionalSelectionValid(): boolean {
        return checkOptionalSelectionValid(this.groupedOptionals, this.selectedOptionalsMap);
    }
}
